/**
 * OrganisationAutocomplete - Typeahead search for organisations
 * Searches database as user types, allows creating new if not found
 */

import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2, Plus, Building2, Check, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { intelligenceAPI } from '../lib/api';
import { toast } from 'sonner';

export function OrganisationAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search or type organisation name...",
  disabled = false,
  className,
}) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    // Click outside handler
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchOrganisations = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await intelligenceAPI.getAll({
        record_type: 'organisation',
        search: searchQuery,
        page_size: 10
      });
      setResults(response.data.records || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
    setSelectedOrg(null);
    setShowDropdown(true);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchOrganisations(newValue);
    }, 300);
  };

  const handleSelect = (org) => {
    setQuery(org.title);
    setSelectedOrg(org);
    onChange?.(org.title);
    onSelect?.({
      id: org.id,
      name: org.title,
      record: org
    });
    setShowDropdown(false);
  };

  const handleCreateNew = async () => {
    if (!query.trim()) return;

    try {
      setCreating(true);
      const response = await intelligenceAPI.create({
        title: query.trim(),
        record_type: 'organisation',
        content: {
          schema_version: 'uk-intel-v1',
          name: query.trim(),
        },
        tags: [],
        source: 'Manual'
      });

      toast.success(`Organisation "${query}" created`);
      
      const newOrg = response.data;
      setSelectedOrg(newOrg);
      onSelect?.({
        id: newOrg.id,
        name: newOrg.title,
        record: newOrg
      });
      setShowDropdown(false);
    } catch (error) {
      toast.error('Failed to create organisation');
    } finally {
      setCreating(false);
    }
  };

  const clearSelection = () => {
    setQuery('');
    setSelectedOrg(null);
    onChange?.('');
    onSelect?.(null);
  };

  const showNoResultsWithCreate = query.length >= 2 && !loading && results.length === 0;
  const hasExactMatch = results.some(r => r.title.toLowerCase() === query.toLowerCase());

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={() => query.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pl-9 pr-8 bg-bg-tertiary border-border-default",
            selectedOrg && "border-green-600"
          )}
          data-testid="org-autocomplete-input"
        />
        {selectedOrg && (
          <Check className="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
        )}
        {query && (
          <button
            onClick={clearSelection}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-bg-tertiary"
          >
            <X className="w-4 h-4 text-text-tertiary" />
          </button>
        )}
      </div>

      {showDropdown && (query.length >= 2 || results.length > 0) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-surface-card border border-border-default rounded-lg shadow-lg overflow-hidden"
        >
          {loading && (
            <div className="p-3 flex items-center justify-center text-text-secondary">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Searching...
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              {results.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelect(org)}
                  className="w-full px-3 py-2 text-left hover:bg-bg-tertiary flex items-center gap-2 border-b border-border-default last:border-0"
                >
                  <Building2 className="w-4 h-4 text-blue-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-text-primary truncate">{org.title}</div>
                    {org.content?.companies_house_number && (
                      <div className="text-xs text-text-tertiary">
                        CH: {org.content.companies_house_number}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-text-tertiary font-mono">
                    {org.id?.slice(0, 8)}...
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Create new option */}
          {!loading && query.length >= 2 && !hasExactMatch && (
            <div className="p-2 border-t border-border-default bg-bg-secondary">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleCreateNew}
                disabled={creating}
                data-testid="create-new-org-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create "{query}" as new organisation
              </Button>
            </div>
          )}

          {showNoResultsWithCreate && !hasExactMatch && (
            <div className="p-3 text-sm text-text-secondary text-center">
              No matching organisations found
            </div>
          )}
        </div>
      )}

      {selectedOrg && (
        <p className="text-xs text-text-tertiary mt-1">
          Linked: {selectedOrg.id}
        </p>
      )}
    </div>
  );
}

export default OrganisationAutocomplete;
